<template>
    <DefaultTemplate :model="model">
        <div class="container">
            <div class="mt-12 mb-8" id="pagetop">
                <Breadcrumbs :breadcrumbs="model.breadcrumbs" className="text-grey-400" />
            </div>

            <div class="block lg:flex">
                <div class="w-full lg:w-3/4 pr-0 lg:pr-4">

                    <article class="p-4 md:px-10 md:py-4 lg:px-20 lg:py-10 mb-4 bg-white">
                        <h1 v-epi-edit="'Name'" class="mb-2">{{ model.name }}</h1>
                        <div v-if="this.model.intro || this.model.mainBody || inEditMode ">
                            <hr class="my-4 lg:my-10" />
                            <div v-epi-edit="'Intro'" class="main-body my-4 article" v-html="this.model.intro" />
                            <div v-epi-edit="'MainBody'" id="main-body" class="main-body my-4 article" v-html="this.model.mainBody" />
                        </div>
                    </article>

                    <!-- Mobile filter -->
                    <div class="sm:hidden flex flex-col justify-end items-end mb-4">
                        <button class="uppercase mb-2" @click="toggleMobileFilterOpen">Filtrer <font-awesome-icon icon="filter" /></button>
                        <div class="w-full ease-in duration-200 delay-75 overflow-hidden"
                             :class="[filterOpen ? 'max-h-96 transition-maxHeight' : 'max-h-0']">
                            <LatestPublicationsFilter :allYears="years" :currentYear="filterYear" @setFilterYear="setFilterYear" />
                        </div>
                    </div>

                    <div v-if="dataLoaded">
                        <div v-if="items === null || items.length <= 0" class="text-warning">
                            Ingen publikasjoner funnet for gjeldende år
                        </div>
                        <div v-else>
                            <ul>
                                <li v-for="(item, index) in items"
                                    :key="$uuid.v4(index)"
                                    :class="[`border-${item.color}-500`]"
                                    class="flex mb-4 bg-white border-t-6 relative">
                                    <a :href="item.url"
                                       class="absolute top-0 left-0 h-full w-full"
                                       title="Les mer">
                                        <span class="sr-only">
                                            Les mer
                                        </span>
                                    </a>
                                    <div class="w-1/6 py-4 md:py-0">
                                        <img :src="`${item.image}?${getImageParams(item)}`"
                                             :alt="item.title" />
                                    </div>
                                    <div class="w-4/6 p-4">
                                        <h3>{{ item.title }}</h3>
                                        <div v-if="item.subHeading">
                                            <p class="py-2 text-sm text-grey-300">[{{ new Date(item.publishDate).toLocaleDateString('nb-NO', dateOptions) }} {{item.subHeading}}]</p>
                                        </div>
                                        <div v-else>
                                            <p class="py-2 text-sm text-grey-300">[{{ new Date(item.publishDate).toLocaleDateString('nb-NO', dateOptions) }} Redaksjonen]</p>
                                            <p class="text-sm pb-2" :inner-html.prop="item.extract" />
                                        </div>
                                    </div>
                                    <div class="w-1/6 text-right p-2">
                                        <span v-if="item.tema" class="text-xs font-bold tracking-wider p-1 text-white rounded-sm bg-green-600">
                                            {{item.tema}}
                                        </span>
                                    </div>
                                </li>
                            </ul>
                            <Pagination v-if="totalPages > 1" :currentPage="currentPage" :totalPages="totalPages" @change="getLatestPublications" />
                        </div>
                    </div>
                    <div v-else class="my-12">
                        <LoadingSpinner size="2x" />
                    </div>
                </div>
                <aside class="w-full lg:w-1/4 pr-0 lg:pl-4 mb-4">
                    <div class="p-5 mb-6 bg-white">
                        <dl class="text-sm">
                            <dt v-epi-edit="'RightColumnHeading'" class="text-green-600 font-semibold pb-4">{{model.rightColumnHeading}}</dt>
                            <dt>
                                <LatestPublicationsFilter :allYears="years" :currentYear="filterYear" @setFilterYear="setFilterYear" />
                            </dt>
                        </dl>
                        <div class="flex justify-end mt-6">
                            <Sharing />
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    </DefaultTemplate>
</template>

<script>
import api from '@/Scripts/api/api';
import LoadingSpinner from '@/Scripts/components/atoms/LoadingSpinner/loadingSpinner.vue';
import EpiLink from '@/Scripts/components/atoms/EpiLink/index.vue';
import Breadcrumbs from '@/Scripts/components/organisms/Breadcrumbs/breadcrumbs.vue';
import Pagination from '@/Scripts/components/organisms/Pagination/pagination.vue';
import LatestPublicationsFilter from '@/Scripts/components/organisms/Filters/latestPublicationsFilter.vue';
import Sharing from '@/Scripts/components/atoms/Sharing/Sharing.vue';
import { mapState } from 'vuex';

export default {
    props: ['model'],
    components: {
        LoadingSpinner,
        EpiLink,
        Breadcrumbs,
        Pagination,
        LatestPublicationsFilter,
        Sharing
    },
    data() {
        const pageSize = (this.model.pageSize && this.model.pageSize.value ? this.model.pageSize.value : 10);
        return {
            dataLoaded: false,
            error: false,
            items: [],
            currentPage: 1,
            totalPages: 0,
            filterYear: 0,
            numPrPage: pageSize,
            dateOptions: { year: 'numeric', month: '2-digit', day: '2-digit' },
            imageParams: 'scale=both&width=180&height=112',
            years: [],
            siteId: this.model.sites.find(x => x.current === true).id, //this.$store.getters.getSiteId <- trøbbel med denne i noen tilfeller ved refresh av siden, derfor endret til å hente fra modellen,
            filterOpen: false
        };
    },
    created() {
        this.setAvailableYears();
        this.getLatestPublications(this.currentPage);
    },
    computed: mapState({
        inEditMode: state => state.epiContext.inEditMode
    }),
    methods: {
        setAvailableYears() {
            const parameters = {
                size: 1,
                page: 1,
                year: 0,
                siteid: this.siteId
            };
            api.getLatestPublications(parameters).then(response => {
                let years = [];
                if (response.status === 200) {
                    if (response.data.FacetDate && response.data.FacetDate.length > 0) {
                        years = response.data.FacetDate.reverse();
                    }
                    console.log(years);
                    this.years = years;
                } else {
                    this.error = true;
                }
            });
        },
        getLatestPublications(page, click = false) {
            this.dataLoaded = false;
            const parameters = {
                size: this.model.pageSize,
                page: page,
                year: this.filterYear,
                siteid: this.siteId
            };
            api.getLatestPublications(parameters).then(response => {
                if (response.status === 200) {
                    this.dataLoaded = true;
                    this.items = response.data.Hits;
                    this.totalPages = this.calculatePageCount(response.data.NumHitsPages);
                    this.currentPage = page;
                    if (click) {
                        document.getElementById('pagetop').scrollIntoView({behavior: 'smooth', block: 'end', inline: 'nearest'});
                    }
                } else {
                    this.error = true;
                }
            });
        },
        setFilterYear(year) {
            this.currentPage = 1;
            this.filterYear = year;
            this.filterOpen = false;
            this.getLatestPublications(this.currentPage);
        },
        getImageParams(item) {
            let params = 'scale=both&width=180&height=180';
            return (item.imageResizerParams ? `${params}&${item.imageResizerParams}` : params);
        },
        calculatePageCount(hitCount) {
            let count = hitCount / this.numPrPage;
            return Math.ceil(count > 0 ? count : 1);
        },
        toggleMobileFilterOpen() {
            this.filterOpen = !this.filterOpen;
        },
        log(msg) {
            console.log(msg);
        }
    }
};
</script>
